import { createSelector } from '@reduxjs/toolkit';
import { VideoBackgroundProcessorType, VideoProcessorOptions } from '@yandex-video-platform/goloom-sdk';

import { RootState } from '../../../../init-store';
import {
    getAppVersionSelector,
    isMessengerIntegration,
    isNoiseCancellationSupportedSelector,
    isRoomAgentMode,
    selectIsOnPremise,
    selectRoomAgentShowMyVideo,
} from '../config/selectors';
import {
    isMobileVirtualBackgroundEnabled,
    selectIsNoiseCancellationFeatureEnabled,
    selectIsTestplane,
} from '../flags/selectors';
import { getUASelector, isMobileUa } from '../uatraits/selectors';
import { getUserSelector } from '../user';
import { isYandexSsoUserSelector, isYandexTeamUserSelector } from '../user/selectors';
import { FacingMode, MediaSettings, StoreDevice, VirtualBackgroundType } from './types';

export const getShowMyVideo = (state: RootState) => {
    return state.settings.media.showMyVideo && (!isRoomAgentMode(state) || selectRoomAgentShowMyVideo(state));
};

export const getDevices = ({ settings }: RootState) => settings.media.devices;

export const getHasDevices = (state: RootState) => getDevices(state).length > 0;

export const getDevicesByTypeSelector = (state: RootState, kind: MediaDeviceKind): StoreDevice[] => {
    return getDevices(state).filter((device) => device.kind === kind);
};

const getDevice = (
    deviceId: string,
    kind: MediaDeviceKind,
    devices: StoreDevice[],
    fallbackToDefault = true
): StoreDevice | undefined => {
    const kindDevices = devices.filter((device) => device.kind === kind);

    return (
        kindDevices.find((device) => device.deviceId === deviceId) ||
        (fallbackToDefault
            ? kindDevices.find((device) => device.deviceId === 'default') || kindDevices?.[0]
            : undefined)
    );
};

export const getDeviceSelector = (
    state: RootState,
    deviceId: string,
    kind: MediaDeviceKind
): StoreDevice | undefined => {
    return getDevice(deviceId, kind, getDevices(state), false);
};

export const getAudioInputDeviceSelector = ({ settings }: RootState): StoreDevice | undefined => {
    return getDevice(settings.media.audioInputDeviceId, 'audioinput', settings.media.devices);
};

export const getAudioOutputDeviceSelector = ({ settings }: RootState): StoreDevice | undefined => {
    return getDevice(settings.media.audioOutputDeviceId, 'audiooutput', settings.media.devices);
};

export const getVideoInputDeviceSelector = ({ settings }: RootState): StoreDevice | undefined => {
    return getDevice(settings.media.videoInputDeviceId, 'videoinput', settings.media.devices);
};

export const getAudioInputDeviceIdSelector = ({ settings }: RootState): string => {
    return settings.media.audioInputDeviceId;
};

export const getMediaSettings = ({ settings }: RootState): MediaSettings => {
    return settings.media;
};

export const getIsShownLimitMessage = (state: RootState): boolean => {
    return state.settings.isShownLimitMessage;
};

const getSettedDevices = (state: RootState): Record<MediaDeviceKind, Maybe<StoreDevice>> => {
    return {
        audioinput: getAudioInputDeviceSelector(state),
        videoinput: getVideoInputDeviceSelector(state),
        audiooutput: getAudioOutputDeviceSelector(state),
    };
};

const getSettedDeviceByKind = (state: RootState, kind: MediaDeviceKind) => {
    return getSettedDevices(state)[kind];
};

export const getHasDeviceAlreadySetted = (state: RootState, device: StoreDevice): boolean => {
    return getSettedDeviceByKind(state, device.kind)?.deviceId === device.deviceId;
};

export const checkDeviceAvailable = (checkDevice: StoreDevice, availableDevices: StoreDevice[]): boolean => {
    return availableDevices.some(({ deviceId, kind, groupId }) => {
        return (
            deviceId === checkDevice.deviceId &&
            kind === checkDevice.kind &&
            (deviceId === 'default' && kind === 'audioinput' ? groupId === checkDevice.groupId : true)
        );
    });
};

export const getIsOutputDeviceChangeAvailable = (state: RootState) => {
    return state.settings.media.isOutputDeviceChangeAvailable;
};

export const getVideoFacingMode = (state: RootState) => {
    return state.settings.media.videoFacingMode;
};

export const getIsVideoShouldBeMirrored = (state: RootState) => {
    const isMobile = isMobileUa(state);
    const isBackCamera = getVideoFacingMode(state) === FacingMode.ENVIRONMENT;
    return !isMobile || !isBackCamera;
};

export const getFeedbackFormVisibility = (state: RootState) => {
    return state.settings.isFeedbackFormVisible;
};

export const selectIsFaqModalVisible = (state: RootState) => state.settings.isFaqModalVisible;

export const getUserInfoForSupportFeedback = (state: RootState) => {
    const user = getUserSelector(state);
    const appVersion = getAppVersionSelector(state);
    const ua = getUASelector(state);

    return {
        login: user?.login ?? '',
        browser: ua.BrowserName,
        browser_version: ua.BrowserVersion,
        os_family: ua.OSFamily,
        os_version: ua.OSVersion || 0,
        telemost_version: appVersion,
    };
};

export const selectNoiseCancellationType = (state: RootState) => {
    return state.settings.media.noiseCancellationType;
};

export const selectIsNoiseCancellationEnabled = (state: RootState) => {
    return (
        !selectIsOnPremise(state) &&
        isNoiseCancellationSupportedSelector(state) &&
        (selectIsNoiseCancellationFeatureEnabled(state) ||
            (!isMessengerIntegration(state) && (isYandexSsoUserSelector(state) || isYandexTeamUserSelector(state))))
    );
};

export const selectIsVirtualBackgroundEnabled = (state: RootState) => {
    const isVirtualBackgroundEnabled =
        !selectIsTestplane(state) &&
        !isMessengerIntegration(state) &&
        (!isMobileUa(state) || isMobileVirtualBackgroundEnabled(state)) &&
        (isYandexSsoUserSelector(state) || isYandexTeamUserSelector(state));

    // Выключено принудительно в рамках TELEMOSTFRONT-2711
    return false && isVirtualBackgroundEnabled;
};

export const selectVirtualBackgroundType = (state: RootState) => state.settings.media.virtualBackgroundType;

const getBackgroundVideoProcessorOptions = (
    virtualBackgroundType: VirtualBackgroundType
): VideoProcessorOptions['backgroundProcessorOptions'] => {
    if (virtualBackgroundType === VirtualBackgroundType.NONE) {
        return false;
    }

    return {
        type: VideoBackgroundProcessorType.BLUR,
    };
};

export const getVideoProcessorOptions = (
    virtualBackgroundType: VirtualBackgroundType,
    virtualBackgroundEnabled = true
): VideoProcessorOptions => ({
    backgroundProcessorOptions: virtualBackgroundEnabled && getBackgroundVideoProcessorOptions(virtualBackgroundType),
});

export const selectVideoProcessorOptions = createSelector(
    [selectVirtualBackgroundType, selectIsVirtualBackgroundEnabled],
    getVideoProcessorOptions
);

export const selectJoinWithCameraOff = (state: RootState) => {
    return state.settings.media.joinWithCameraOff;
};

export const selectJoinWithMicrophoneOff = (state: RootState) => {
    return state.settings.media.joinWithMicrophoneOff;
};

export const selectIsSettingsReady = (state: RootState) => {
    return state.settings.isSettingsReady;
};

export const selectHideParticipantsVideo = (state: RootState) => {
    return state.settings.media.hideParticipantsVideo;
};

export const selectWasParticipantsVideoNotificationShown = (state: RootState) => {
    return state.settings.wasParticipantsVideoNotificationShown;
};

export const selectSummarizationWelcomePopupWasShown = (state: RootState) => {
    return state.settings.summarizationWelcomePopupWasShown;
};

export const selectSummarizationStartPopupWasShown = (state: RootState) => {
    return state.settings.summarizationStartPopupWasShown;
};

export const selectIsAudioInputDiagnosticRun = (state: RootState) => {
    return state.settings.isAudioInputDiagnosticRun;
};

export const selectIsAudioOutputDiagnosticRun = (state: RootState) => {
    return state.settings.isAudioOutputDiagnosticRun;
};

export const selectBandwidthEffective4kCodec = (state: RootState) => {
    return state.settings.media.bandwidthEffective4kCodec;
};
